/*
 * MODIFICATION HISTORY - 2025-11-12
 * 
 * ISSUE: PDF pagination was creating blank pages when content exceeded page limits.
 * Symptoms:
 * - Long content (Advice, Examination, etc.) would move entire section to next page, leaving previous page blank
 * - Table forced to always start on page 2 via 'break' attribute, causing blank page 2 when page 1 overflowed
 * - Disclaimer on separate page when no medications, showing same blank page behavior
 * - Table headers not repeating when table split across pages
 * 
 * ROOT CAUSES:
 * 1. All content sections used wrap={false}, making large blocks unbreakable
 * 2. Medication table had 'break' attribute forcing it to new page
 * 3. Disclaimer logic created separate <Page> for no-medication case
 * 4. Table headers lacked 'fixed' prop for pagination
 * 
 * SOLUTIONS APPLIED:
 * 1. Removed wrap={false} from all content sections (Chief Complaints, History, Investigation, 
 *    Examination, Diagnosis, Advice) - now use styles.section for natural flow
 * 2. Kept keepWithNext: true in sectionTitle style to prevent heading orphans
 * 3. Added 'fixed' prop to table headers so they repeat on every page when table splits
 * 4. Removed 'break' attribute from medication table for natural content flow
 * 5. Unified disclaimer logic: show on page 1 if medicationChunks.length <= 1
 * 6. Removed separate disclaimer page for no-medication case
 * 7. Changed all disclaimer containers from inline styles to styles.section
 * 
 * RESULT: Natural pagination flow, no blank pages, table headers repeat on splits
 */

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingTop: 150,
    paddingBottom: 80,
    fontSize: 10,
    lineHeight: 1.3,
    fontFamily: "Helvetica",
    position: "relative",
  },
  title: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 15,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 5,
    fontWeight: "bold",
    marginTop: 10,
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: 12,
    marginBottom: 4,
    fontWeight: "bold",
    marginTop: 10,
    keepWithNext: true, 
  },
  boldfont: {
    fontSize: 10,
    fontWeight: "bold",
  },
  text: {
    marginBottom: 5,
    fontSize: 10,
  },
  /* MODIFICATION: 2025-11-12 - Removed border from table container
     OLD CODE: borderWidth: 1 caused vertical lines to extend down the page on table splits
     NEW CODE: Removed border properties, relying on tableCol borders to create grid
  */
  table: {
    display: "table",
    width: "100%",
    marginBottom: 10,
    marginTop: 5,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    padding: 5,
    fontSize: 10,
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    fontSize: 10,
  },
  section: {
    marginBottom: 10,
  },
  patientInfoContainer: {
    flexDirection: "row",
    marginBottom: 10,
  },
  patientInfoLeft: {
    width: "50%",
  },
  patientInfoRight: {
    width: "50%",
    textAlign: "right",
  },
  patientInfoItem: {
    marginBottom: 5,
  },
  hr: {
    borderBottomWidth: 1,
    borderBottomColor: "#333",
    marginBottom: 10,
    marginTop: 10,
  },
  signatureArea: {
    marginTop: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signatureBox: {
    width: "45%",
    borderTopWidth: 1,
    borderTopColor: "#000",
    paddingTop: 5,
  },
  disclaimerImage: {
    width: "100%",
    marginTop: 10,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: "right",
    fontSize: 9,
    color: "#333",
  },
});




const OPDDischargePDF = ({ patient, consultation, prescriptions }) => {
  // prescriptions prop is now the direct array for this consultation from the new API
  const patientData = patient || {};
  const consultationData = consultation || {};
  const medicationList = prescriptions || [];
  
  const currentDate = new Date().toISOString().split("T")[0];
  const currentTime = new Date().toTimeString().split(" ")[0];
  
  // Format consultation created_at date
  let consultationDate = "";
  if (consultationData.created_at) {
    const dateObj = new Date(consultationData.created_at);
    if (!isNaN(dateObj)) {
      // Format as dd/mm/yyyy
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      consultationDate = `${day}/${month}/${year}`;
    } else {
      consultationDate = consultationData.created_at;
    }
  }

  const centerCode =
    typeof window !== "undefined"
      ? localStorage.getItem("center_code") || ""
      : "";

  let disclaimerImage = "/TeluguDisclaimer.png";
  if (centerCode.startsWith("CHE")) disclaimerImage = "/TamilDisclaimer.png";
  else if (centerCode.startsWith("BAN"))
    disclaimerImage = "/KannadaDisclaimer.png";

  // Filter valid medications
  const validMedications = medicationList.filter((med) => med.medicine_name?.trim());

  // Chunk medications into pages (estimate ~15 rows per page for A4 with standard margins)
  // Configurable: adjust ROWS_PER_PAGE based on your actual row height and page layout
  const ROWS_PER_PAGE = 15;
  const medicationChunks = [];
  for (let i = 0; i < validMedications.length; i += ROWS_PER_PAGE) {
    medicationChunks.push(validMedications.slice(i, i + ROWS_PER_PAGE));
  }

  /* MODIFICATION: 2025-11-12 - Added fixed table header for pagination
     OLD FAULTY CODE: Table header was not marked as 'fixed', so when table split 
     across pages, subsequent pages would not show the header, making it hard to read.
     
     OLD CODE:
     <View style={styles.tableRow}>
       <View style={[styles.tableCol, styles.tableHeader, { width: "10%" }]}>
         <Text>S.No</Text>
       </View>
       ...
     </View>
     
     NEW APPROACH: Added 'fixed' prop to table header so it repeats on every page
     when the medication table splits across multiple pages.
  */

  // Helper to render table header (reusable for splits)
  const renderTableHeader = () => (
    <View style={styles.tableRow} fixed>
      <View style={[styles.tableCol, styles.tableHeader, { width: "10%" }]}>
        <Text>S.No</Text>
      </View>
      <View style={[styles.tableCol, styles.tableHeader, { width: "30%" }]}>
        <Text>Medicine Name</Text>
      </View>
      <View style={[styles.tableCol, styles.tableHeader, { width: "10%" }]}>
        <Text>M</Text>
      </View>
      <View style={[styles.tableCol, styles.tableHeader, { width: "10%" }]}>
        <Text>A</Text>
      </View>
      <View style={[styles.tableCol, styles.tableHeader, { width: "10%" }]}>
        <Text>N</Text>
      </View>
      <View style={[styles.tableCol, styles.tableHeader, { width: "30%" }]}>
        <Text>Instructions</Text>
      </View>
    </View>
  );

  // Helper to render a medication table for a given chunk
  const renderMedicationTable = (chunk, startIndex) => (
    <View style={styles.table}>
      {/* Header - fixed so it repeats on page splits */}
      {renderTableHeader()}
      {/* Data Rows */}
      {chunk.map((med, idx) => {
        const notes = med.additional_notes?.trim();
        const duration = med.duration_days?.trim();
        const instructions = [
          notes,
          duration && duration !== "null" && duration !== ""
            ? `for ${duration}`
            : "",
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        return (
          <View style={styles.tableRow} key={startIndex + idx} wrap={false}>
            <View style={[styles.tableCol, { width: "10%" }]}>
              <Text>{startIndex + idx + 1}</Text>
            </View>
            <View style={[styles.tableCol, { width: "30%" }]}>
              <Text>{med.medicine_name}</Text>
            </View>
            <View style={[styles.tableCol, { width: "10%" }]}>
              <Text>{med.morning ? "Yes" : "No"}</Text>
            </View>
            <View style={[styles.tableCol, { width: "10%" }]}>
              <Text>{med.afternoon ? "Yes" : "No"}</Text>
            </View>
            <View style={[styles.tableCol, { width: "10%" }]}>
              <Text>{med.night ? "Yes" : "No"}</Text>
            </View>
            <View style={[styles.tableCol, { width: "30%" }]}>
              <Text>{instructions || " "}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Modification 19/18/2025: client asked to remove title from this discharge summary */}
        {/* <Text style={styles.title}>OPD DISCHARGE FORM</Text> */}
        
        {/* Consultation Date */}
        <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 5 }}>
          <View style={{ width: "50%" }}>
            {consultationDate && (
              <Text style={[styles.boldfont, { fontSize: 10 }]}>Date: {consultationDate}</Text>
            )}
          </View>
        </View>
        <View style={styles.hr} />

        {/* Patient Info */}
        <View style={styles.patientInfoContainer}>
          <View style={styles.patientInfoLeft}>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Name: {patientData.full_name || "______________"}
            </Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Age: {patientData.age || " ______________"}
            </Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Sex: {patientData.gender || " ______________"}
            </Text>
            {/* <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Occupation: {patientData.occupation || "______________ "}
            </Text> */}
          </View>
          <View style={styles.patientInfoRight}>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Phone: {patientData.mobile || "______________ "}
            </Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>
              UHID: {patientData.uhid_no || " ______________"}
            </Text>
            {/* <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Religion: {patientData.religion || " ______________"}
            </Text> */}
            <Text style={[styles.boldfont, styles.patientInfoItem]}>
              Identification Marks: {" "}
              {patientData.identification_mark || " ______________"}
            </Text>
          </View>
        </View>

        <View style={styles.hr} />

        {/* Consultation */}
        <Text style={styles.subtitle}>CONSULTATION</Text>

        {/* MODIFICATION: 2025-11-12 - Removed wrap={false} from all sections to prevent blank pages
            OLD FAULTY CODE: All sections wrapped with <View wrap={false}> causing entire blocks 
            to move to next page when content exceeded available space, leaving blank pages.
            
            Example of old code:
            <View wrap={false}>
              <Text style={styles.sectionTitle}>Chief Complaints</Text>
              <Text style={styles.text}>{consultationData.chief_complaints || " "}</Text>
            </View>
            
            NEW APPROACH: Use styles.section to allow natural content flow across pages.
            Headings stay with first line via keepWithNext: true in sectionTitle style.
        */}

        {/* Chief Complaints */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Chief Complaints</Text>
          <Text style={styles.text}>
            {consultationData.chief_complaints || " "}
          </Text>
        </View>

        <View style={styles.hr} />

        {/* History of Illness */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>History of Illness</Text>
          <Text style={styles.text}>
            {consultationData.history_of_illness || " "}
          </Text>
        </View>

        {/* Pain Scale */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Pain Scale: {consultationData.pain_scale || " "}
          </Text>
        </View>

        <View style={styles.hr} />

        {/* Investigation */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Investigation</Text>
          <Text style={styles.text}>{consultationData.investigation || " "}</Text>
        </View>
        
        <View style={styles.hr} />
        
        {/* Examination */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Examination</Text>
          <Text style={styles.text}>{consultationData.examination || " "}</Text>
        </View>
        
        <View style={styles.hr} />

        {/* Provisional Diagnosis */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Provisional Diagnosis</Text>
          <Text style={styles.text}>
            {consultationData.provisional_diagnosis || " "}
          </Text>
        </View>
        
        <View style={styles.hr} />

        {/* Advice */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Advice</Text>
          <Text style={styles.text}>{consultationData.advice || " "}</Text>
        </View>
        
        <View style={styles.hr} />

        {/* MODIFICATION: 2025-11-12 - Removed forced page break and unified disclaimer logic
            OLD FAULTY CODE 1: Table had 'break' attribute forcing it to always start on page 2
            <View style={styles.section} break>
              <Text style={styles.sectionTitle}>Prescribed Medication</Text>
              {renderMedicationTable(medicationChunks[0], 0)}
            </View>
            This caused blank page 2 when page 1 content overflowed to page 2.
            
            OLD FAULTY CODE 2: Disclaimer had separate page for no-medication case
            {medicationChunks.length === 0 && (patientData.disclaimer === true || patientData.disclaimer === "true") && (
              <Page size="A4" style={styles.page}>
                <View>
                  <Text style={styles.sectionTitle}>Disclaimer</Text>
                  <Image src={disclaimerImage} style={styles.disclaimerImage} />
                </View>
              </Page>
            )}
            
            NEW APPROACH: 
            - Removed 'break' to allow natural content flow
            - Unified disclaimer logic: show on page 1 if 0 or 1 medication chunks
            - All disclaimer blocks use styles.section for consistent spacing
        */}

        {/* MODIFICATION: 2025-11-13 - Fixed table header squishing and orphaned heading
            ISSUE: "Prescribed Medication" heading could be orphaned at page bottom while
            table moves to next page. Additionally, if table starts near page end, header
            row gets vertically compressed.
            SOLUTION: Wrap heading + table + first data row in wrap={false} to ensure
            they stay together as a unit when page breaks occur.
        */}

        {/* Prescribed Medication - First Chunk */}
        {medicationChunks.length > 0 && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>Prescribed Medication</Text>
            {renderMedicationTable(medicationChunks[0], 0)}
          </View>
        )}

        {/* Disclaimer on first page if one medication chunk OR no medications - MANDATORY based on center code */}
        {(medicationChunks.length <= 1) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Disclaimer</Text>
            <Image src={disclaimerImage} style={styles.disclaimerImage} />
          </View>
        )}

        <Text
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          style={styles.footer}
          fixed
        />
      </Page>

      {/* Additional pages for remaining medication chunks */}
      {medicationChunks.slice(1).map((chunk, chunkIndex) => {
        const isLastChunk = chunkIndex === medicationChunks.length - 2;
        const showDisclaimer = isLastChunk;
        
        return (
          <Page size="A4" style={styles.page} key={`med-page-${chunkIndex + 1}`}>
            {/* MODIFICATION: 2025-11-13 - Added wrap={false} for consistency
                Ensures heading stays with table on continuation pages as well
            */}
            <View style={styles.section} wrap={false}>
              <Text style={styles.sectionTitle}>
                Prescribed Medication (continued)
              </Text>
              {renderMedicationTable(chunk, (chunkIndex + 1) * ROWS_PER_PAGE)}
            </View>

            {/* MODIFICATION: 2025-11-12 - Changed disclaimer container to use styles.section
                OLD CODE: <View style={{ marginTop: 20 }}>
                NEW CODE: <View style={styles.section}>
                This ensures consistent spacing and allows natural pagination flow.
            */}
            {/* Disclaimer on last medication page if enabled */}
            {showDisclaimer && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Disclaimer</Text>
                <Image src={disclaimerImage} style={styles.disclaimerImage} />
              </View>
            )}

            <Text
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} of ${totalPages}`
              }
              style={styles.footer}
              fixed
            />
          </Page>
        );
      })}

    </Document>
  );
};

export default OPDDischargePDF;

